import { faArrowLeft, faFileInvoice } from '@fortawesome/pro-regular-svg-icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar } from 'src/components/Avatar';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Chip } from 'src/components/Chip';
import { Divider } from 'src/components/Divider';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { Loader } from 'src/components/Loader';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { Cart } from 'src/models/cart/_types';
import { Invoice } from 'src/models/payment/_types';
import {
  fetchInvoices,
  isPayableOpenInvoice,
  pastDueInvoicesQueryKey,
  payOpenInvoicesWithPaymentMethod,
} from 'src/models/payment/payment';
import { PaymentMethod } from 'src/models/payment-methods/_types';
import { User } from 'src/models/user/_types';
import { fetchUserById } from 'src/models/user/user';
import { getUserDisplayName, getUserInitials } from 'src/models/user/utils';
import PaymentMethodSelector from 'src/pages/Clients/PaymentDialog/PaymentMethodSelector';
import { SidebarDialog, SidebarDialogContent } from 'src/shared/SidebarDialog';
import { formatFullDateAndTime } from 'src/utils/dateFormatter';
import { formatCurrency, formatNumber } from 'src/utils/numberFormatter';
import toast from 'src/utils/toast';

function getPayablePaymentMethodId(
  method: string | PaymentMethod | undefined
): string | undefined {
  if (!method) return undefined;
  if (typeof method === 'object') {
    if (
      method.paymentMethodType === 'card' ||
      method.paymentMethodType === 'us_bank_account'
    ) {
      return String(method.id);
    }
    return undefined;
  }
  const excluded = new Set([
    'cash',
    'check',
    'free',
    'tap_to_pay',
    'card_present',
  ]);
  if (excluded.has(method)) return undefined;
  return method;
}

function buildPlaceholderCart(userId: User['id'], currency: string): Cart {
  return {
    userId,
    currency,
    cartItems: [
      {
        quantity: 1,
        itemType: 'StripeSubscription',
        paymentOption: {
          type: 'subscription',
          amount: 0,
          name: null,
          currency,
          location: null,
        },
      },
    ],
  };
}

function getPaymentMethodLabel(method: string | PaymentMethod | undefined) {
  if (!method) return '';
  if (typeof method === 'string') return method;
  if (method.brand) return `${method.brand} ${method.last4}`;
  return method.primary || method.paymentMethodType;
}

function getFormattedAmount(amount: number, currency: string) {
  return formatCurrency(amount, { currency: currency.toUpperCase() });
}

function getInvoicePrimaryLabel(invoice: Invoice) {
  return invoice.itemName || invoice.description || invoice.name;
}

function applicationFeeInDollars(invoice: Invoice): number {
  const raw = invoice.applicationFeeAmount;
  if (raw == null || raw === 0) return 0;
  return raw / 100;
}

/** Estimates merchandise subtotal, tax, and app fee from total due (Stripe-style: total ≈ subtotal + tax + fee). */
function merchandiseBreakdownForInvoice(invoice: Invoice) {
  const total = Math.max(0, invoice.amountDue ?? 0);
  const fee = Math.min(Math.max(0, applicationFeeInDollars(invoice)), total);
  const remainder = Math.max(0, total - fee);
  const taxRate = (invoice.taxPercent ?? 0) / 100;
  if (taxRate > 0) {
    const subtotal = remainder / (1 + taxRate);
    const tax = remainder - subtotal;
    return { subtotal, tax, fee, total };
  }
  return { subtotal: remainder, tax: 0, fee, total };
}

function getInvoiceCardTitle(invoice: Invoice) {
  const primary = getInvoicePrimaryLabel(invoice);
  if (
    invoice.itemName &&
    invoice.description &&
    invoice.itemName !== invoice.description
  ) {
    return `${invoice.itemName} - ${invoice.description}`;
  }
  return primary;
}

function PastDueInvoiceDetailCard({
  invoice,
  payable,
  primaryCurrency,
  memberUser,
}: {
  invoice: Invoice;
  payable: boolean;
  primaryCurrency: string;
  memberUser: User | undefined;
}) {
  const { t } = useTranslation('translation', { keyPrefix: 'checkinBlocker' });
  const rowCurrency = (invoice.currency ?? primaryCurrency).toUpperCase();
  const merch = payable
    ? merchandiseBreakdownForInvoice(invoice)
    : {
        subtotal: 0,
        tax: 0,
        fee: 0,
        total: invoice.amountDue ?? 0,
      };
  const cardTitle = getInvoiceCardTitle(invoice);
  const displayLinePrice = payable ? merch.subtotal : (invoice.amountDue ?? 0);

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'neutral.borderRegular',
        borderRadius: 2,
        boxShadow: 1,
        p: 2,
      }}
    >
      <Stack spacing={2}>
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <Box
            sx={{
              alignItems: 'center',
              bgcolor: payable
                ? 'success.backgroundSoft'
                : 'neutral.backgroundSoft',
              borderRadius: 1,
              display: 'flex',
              flexShrink: 0,
              height: 56,
              justifyContent: 'center',
              width: 56,
            }}
          >
            <Icon
              icon={faFileInvoice}
              color={payable ? 'success' : 'neutral'}
              size="medium"
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1">{cardTitle}</Typography>
          </Box>
          <Typography variant="subtitle1" sx={{ flexShrink: 0, pt: 0.25 }}>
            {getFormattedAmount(displayLinePrice, rowCurrency)}
          </Typography>
        </Stack>

        <Stack spacing={1}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="baseline"
          >
            <Typography variant="body2" color="text.secondary">
              {t('pastDuePaymentSummary.metaItemType')}
            </Typography>
            <Typography variant="body2" textAlign="right">
              {invoice.itemType?.trim()
                ? invoice.itemType
                : t('pastDuePaymentSummary.pastDueInvoiceProductType')}
            </Typography>
          </Stack>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="baseline"
          >
            <Typography variant="body2" color="text.secondary">
              {t('pastDuePaymentSummary.metaItemPrice')}
            </Typography>
            <Typography variant="body2" textAlign="right">
              {payable ? getFormattedAmount(merch.subtotal, rowCurrency) : '—'}
            </Typography>
          </Stack>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="baseline"
          >
            <Typography variant="body2" color="text.secondary">
              {t('pastDuePaymentSummary.metaQuantity')}
            </Typography>
            <Typography variant="body2" textAlign="right">
              {payable ? formatNumber(1, { minDigits: 0, maxDigits: 0 }) : '—'}
            </Typography>
          </Stack>
        </Stack>

        {(invoice.dueDate ||
          invoice.subscriptionId ||
          (invoice.paidById &&
            memberUser &&
            String(invoice.paidById) !== String(memberUser.id)) ||
          !payable) && (
          <Stack spacing={0.5}>
            {invoice.dueDate && (
              <Typography variant="caption" color="text.secondary">
                {t('pastDuePaymentSummary.due', {
                  date: formatFullDateAndTime(invoice.dueDate),
                })}
              </Typography>
            )}
            {invoice.subscriptionId && (
              <Typography variant="caption" color="text.secondary">
                {t('pastDuePaymentSummary.fromSubscription')}
              </Typography>
            )}
            {invoice.paidById &&
              memberUser &&
              String(invoice.paidById) !== String(memberUser.id) && (
                <Typography variant="caption" color="text.secondary">
                  {t('pastDuePaymentSummary.paidBy', {
                    name:
                      invoice.paidByName || getUserDisplayName(invoice.paidBy),
                  })}
                </Typography>
              )}
            {!payable && (
              <Typography variant="caption" color="text.secondary">
                {t('pastDuePaymentSummary.notCharged', {
                  status: invoice.status,
                })}
              </Typography>
            )}
          </Stack>
        )}
      </Stack>
    </Box>
  );
}

export function CheckinPastDuePaymentDialog({
  open,
  onClose,
  onPaymentResolved,
  userId,
}: {
  open: boolean;
  onClose: () => void;
  onPaymentResolved?: () => void;
  userId: string;
}) {
  const { t } = useTranslation('translation', { keyPrefix: 'checkinBlocker' });
  const { t: tPaymentDialog } = useTranslation('translation', {
    keyPrefix: 'paymentDialog',
  });
  const queryClient = useQueryClient();

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<
    string | PaymentMethod | undefined
  >();
  const [reviewingOrder, setReviewingOrder] = useState(false);

  useEffect(() => {
    if (!open) {
      setSelectedPaymentMethod(undefined);
      setReviewingOrder(false);
    }
  }, [open]);

  const { data: memberUser, isLoading: isLoadingUser } = useQuery({
    queryKey: ['user', userId],
    queryFn: () => fetchUserById(userId),
    enabled: open && !!userId,
  });

  const { data: invoicePayload, isLoading: isLoadingInvoices } = useQuery({
    queryKey: pastDueInvoicesQueryKey(userId),
    queryFn: () =>
      fetchInvoices({
        userId,
        per: 100,
        filters: { pastDue: true },
      }),
    enabled: open && !!userId,
  });

  const invoices: Invoice[] = invoicePayload?.invoices ?? [];

  const payableInvoices = invoices.filter(isPayableOpenInvoice);
  const excludedInvoices = invoices.filter((inv) => !isPayableOpenInvoice(inv));
  const primaryCurrency =
    payableInvoices[0]?.currency ?? invoices[0]?.currency ?? 'usd';

  const cart = memberUser
    ? buildPlaceholderCart(memberUser.id, primaryCurrency)
    : buildPlaceholderCart(userId, primaryCurrency);

  const payableInvoiceIds = payableInvoices.map((inv) => inv.id);
  const totalAmountDue = payableInvoices.reduce(
    (acc, invoice) => acc + (invoice.amountDue ?? 0),
    0
  );
  const formattedTotalAmountDue = getFormattedAmount(
    totalAmountDue,
    primaryCurrency
  );

  const breakdownTotals = payableInvoices.reduce(
    (acc, inv) => {
      const line = merchandiseBreakdownForInvoice(inv);
      return {
        subtotal: acc.subtotal + line.subtotal,
        tax: acc.tax + line.tax,
        fee: acc.fee + line.fee,
      };
    },
    { subtotal: 0, tax: 0, fee: 0 }
  );
  const taxPercents = Array.from(
    new Set(
      payableInvoices
        .filter((inv) => (inv.taxPercent ?? 0) > 0)
        .map((inv) => Number(inv.taxPercent))
    )
  ).sort((a, b) => a - b);
  const taxPercentForLabel =
    taxPercents.length === 1
      ? Number.isInteger(taxPercents[0])
        ? String(taxPercents[0])
        : String(Math.round(taxPercents[0] * 100) / 100)
      : '';
  const aggregatedTaxLabel =
    taxPercents.length === 1
      ? t('pastDuePaymentSummary.taxLine', { percent: taxPercentForLabel })
      : t('pastDuePaymentSummary.tax');

  const paymentMethodId = getPayablePaymentMethodId(selectedPaymentMethod);

  const payMutation = useMutation({
    mutationFn: async () => {
      if (!paymentMethodId) return;
      await payOpenInvoicesWithPaymentMethod({
        invoices,
        paymentMethodId,
      });
    },
    onSuccess: () => {
      toast.success(t('pastDuePaymentSuccess'));
      onPaymentResolved?.();
      queryClient.invalidateQueries({
        queryKey: pastDueInvoicesQueryKey(userId),
      });
      queryClient.invalidateQueries({ queryKey: ['checkinBlockers'] });
      queryClient.invalidateQueries({
        queryKey: ['paymentMethods', userId],
      });
      queryClient.invalidateQueries({
        queryKey: ['paymentMethods', 'accessible', userId],
      });
      onClose();
    },
    onError: (error: Error) => {
      toast.error(error.message || t('pastDuePaymentError'));
    },
  });

  const canSubmit =
    !!paymentMethodId && payableInvoiceIds.length > 0 && !payMutation.isPending;

  const handlePrimaryClick = () => {
    if (!canSubmit) return;

    if (!reviewingOrder) {
      setReviewingOrder(true);
      return;
    }

    payMutation.mutate();
  };

  return (
    <SidebarDialog
      open={open}
      onClose={onClose}
      title={
        reviewingOrder
          ? tPaymentDialog('orderSummary')
          : tPaymentDialog('paymentMethod')
      }
      leftAdornment={
        reviewingOrder ? (
          <IconButton
            color="secondary"
            onClick={() => setReviewingOrder(false)}
          >
            <Icon icon={faArrowLeft} />
          </IconButton>
        ) : undefined
      }
      maxWidth="md"
      actions={
        !reviewingOrder ? (
          <Button
            fullWidth
            variant="prominent"
            disabled={!canSubmit}
            onClick={handlePrimaryClick}
          >
            {tPaymentDialog('reviewOrder')}
          </Button>
        ) : undefined
      }
    >
      <SidebarDialogContent>
        {isLoadingUser || isLoadingInvoices || !memberUser ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              py: 8,
            }}
          >
            <Loader size="large" variant="circular" />
          </Box>
        ) : reviewingOrder ? (
          <Stack spacing={3} sx={{ pt: 1 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Avatar
                  src={memberUser.profileImage}
                  initials={getUserInitials(memberUser)}
                  size="medium"
                />
                <Typography variant="h6">
                  {getUserDisplayName(memberUser)}
                </Typography>
              </Stack>

              <Chip
                label={t('pastDuePaymentSummary.itemCount', {
                  count: payableInvoices.length,
                })}
                variant="outlined"
                color="success"
              />
            </Stack>

            {payableInvoices.length === 0 && invoices.length > 0 && (
              <Typography variant="body2" color="text.secondary">
                {t('pastDuePaymentSummary.noPayableBalance')}
              </Typography>
            )}

            <Button
              fullWidth
              variant="prominent"
              disabled={!canSubmit}
              loading={payMutation.isPending}
              onClick={handlePrimaryClick}
            >
              {t('actions.chargeBalance', {
                amount: formattedTotalAmountDue,
              })}
            </Button>

            <Box
              sx={{
                border: 1,
                borderColor: 'neutral.borderRegular',
                borderRadius: 2,
                p: 2,
              }}
            >
              <Stack spacing={1.5}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    {t('pastDuePaymentSummary.subtotal')}
                  </Typography>
                  <Typography variant="body2">
                    {getFormattedAmount(
                      breakdownTotals.subtotal,
                      primaryCurrency
                    )}
                  </Typography>
                </Stack>
                <Stack spacing={0.5}>
                  {payableInvoices.map((invoice) => (
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      key={invoice.id}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {getInvoicePrimaryLabel(invoice)}
                      </Typography>
                      <Typography variant="body2">
                        {getFormattedAmount(
                          invoice.amountDue ?? 0,
                          primaryCurrency
                        )}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
                {breakdownTotals.tax >= 0.005 && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      {aggregatedTaxLabel}
                    </Typography>
                    <Typography variant="body2">
                      {getFormattedAmount(breakdownTotals.tax, primaryCurrency)}
                    </Typography>
                  </Stack>
                )}
                {breakdownTotals.fee >= 0.005 && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      {t('pastDuePaymentSummary.applicationFee')}
                    </Typography>
                    <Typography variant="body2">
                      {getFormattedAmount(breakdownTotals.fee, primaryCurrency)}
                    </Typography>
                  </Stack>
                )}

                <Divider />
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="h6">
                    {t('pastDuePaymentSummary.total')}
                  </Typography>
                  <Typography variant="h6">
                    {formattedTotalAmountDue}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    {t('pastDuePaymentSummary.paymentMethod')}
                  </Typography>
                  <Typography variant="body2">
                    {getPaymentMethodLabel(selectedPaymentMethod)}
                  </Typography>
                </Stack>
              </Stack>
            </Box>

            <Stack
              direction="row"
              justifyContent="center"
              alignItems="center"
              spacing={1}
            >
              <Typography variant="h6" component="span">
                {t('pastDuePaymentSummary.orderDetails')}
              </Typography>
              <Button
                color="success"
                variant="borderless"
                onClick={() => setReviewingOrder(false)}
                sx={{ minWidth: 0, p: 0 }}
              >
                {t('pastDuePaymentSummary.edit')}
              </Button>
            </Stack>

            <Stack spacing={2}>
              {payableInvoices.map((invoice) => (
                <PastDueInvoiceDetailCard
                  key={invoice.id}
                  invoice={invoice}
                  payable
                  primaryCurrency={primaryCurrency}
                  memberUser={memberUser}
                />
              ))}
            </Stack>

            {excludedInvoices.length > 0 && (
              <Stack spacing={1.5}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t('pastDuePaymentSummary.notIncludedHeading')}
                </Typography>
                <Stack spacing={2}>
                  {excludedInvoices.map((invoice) => (
                    <PastDueInvoiceDetailCard
                      key={invoice.id}
                      invoice={invoice}
                      payable={false}
                      primaryCurrency={primaryCurrency}
                      memberUser={memberUser}
                    />
                  ))}
                </Stack>
              </Stack>
            )}
          </Stack>
        ) : (
          <PaymentMethodSelector
            cart={cart}
            hideCardPresentReaders
            user={memberUser}
            value={
              typeof selectedPaymentMethod === 'object'
                ? selectedPaymentMethod?.id
                : selectedPaymentMethod
            }
            onSelect={(method) => setSelectedPaymentMethod(method)}
            onCheckNumberChange={() => undefined}
            goToSelectUser={() => undefined}
          />
        )}
      </SidebarDialogContent>
    </SidebarDialog>
  );
}
